import React, { useEffect, useRef, useState } from "react";
import ReactCrop, {
  Crop,
  PercentCrop,
  PixelCrop,
  convertToPixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button, Modal } from "../../../components/ui";
import { cropImageToFile } from "../cropImage";

/** Where the drag handles start out — generous rather than the whole
 *  picture, so there's a border to see and adjust rather than nothing to
 *  grab onto. */
const INITIAL_CROP: PercentCrop = { unit: "%", x: 5, y: 5, width: 90, height: 90 };

type Mode = "crop" | "whole";

interface LogoCropModalProps {
  open: boolean;
  imageSrc: string;
  file: File;
  onCancel: () => void;
  onConfirm: (file: File, dataUrl: string) => void;
}

/**
 * Opens the moment a logo is picked. The selection is entirely the user's —
 * drag to move it, drag a handle to resize it to whatever shape they want —
 * and whatever gets selected is saved at its own resolution, not resampled
 * to a size this dialog decided in advance. "Whole logo" skips cropping
 * altogether for artwork that shouldn't lose any of itself.
 */
const LogoCropModal: React.FC<LogoCropModalProps> = ({
  open,
  imageSrc,
  file,
  onCancel,
  onConfirm,
}) => {
  const [mode, setMode] = useState<Mode>("crop");
  const [crop, setCrop] = useState<Crop | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(
    undefined
  );
  const [saving, setSaving] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Reset on every fresh picture — a second logo must not inherit the
  // first one's selection or mode.
  useEffect(() => {
    if (!open) return;
    setMode("crop");
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, [open, imageSrc]);

  // The selection has to exist before it can be confirmed, so it's seeded
  // the moment there's an image to seed it against — not left for the user
  // to first have to draw one from nothing.
  const seedCrop = (image: HTMLImageElement) => {
    if (!image.width || !image.height) return;
    setCrop(INITIAL_CROP);
    setCompletedCrop(convertToPixelCrop(INITIAL_CROP, image.width, image.height));
  };

  const onImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    seedCrop(event.currentTarget);
  };

  // `onLoad` alone is what left the crop tool with no selection box some of
  // the time: a `data:` URL the browser finishes decoding fast — sometimes
  // before React has even attached the listener — never fires it again, so
  // nothing seeds the selection and there is nothing to see or confirm.
  // Checked directly here as a fallback on every render the crop view is
  // showing; guarded on `crop` so it never overwrites a selection the user
  // has already made, e.g. by switching to "Whole logo" and back.
  useEffect(() => {
    if (mode !== "crop" || crop) return;
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth > 0) {
      seedCrop(image);
    }
  }, [mode, imageSrc, crop]);

  const confirm = async () => {
    if (mode === "whole") {
      onConfirm(file, imageSrc);
      return;
    }
    const image = imageRef.current;
    if (!completedCrop || !image) return;

    setSaving(true);
    try {
      // The selection is reported against the *displayed* image; scaled up
      // to the natural pixels the export actually draws from.
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const { file: croppedFile, dataUrl } = await cropImageToFile(
        imageSrc,
        {
          x: completedCrop.x * scaleX,
          y: completedCrop.y * scaleY,
          width: completedCrop.width * scaleX,
          height: completedCrop.height * scaleY,
        },
        file.name
      );
      onConfirm(croppedFile, dataUrl);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Crop your logo"
      onClose={() => {
        if (!saving) onCancel();
      }}
      dismissOnBackdrop={!saving}
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={saving}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" loading={saving} onClick={confirm}>
            {mode === "whole" ? "Use whole logo" : "Use this crop"}
          </Button>
        </>
      }
    >
      <div className="mb-3 inline-flex rounded-[6px] border border-line bg-[#F4F7F8] p-0.5">
        <button
          type="button"
          onClick={() => setMode("crop")}
          className={`rounded-[5px] px-3 py-1.5 text-[12px] font-medium transition-colors ${
            mode === "crop" ? "bg-white text-ink shadow-sm" : "text-ink-subtle"
          }`}
        >
          Crop
        </button>
        <button
          type="button"
          onClick={() => setMode("whole")}
          className={`rounded-[5px] px-3 py-1.5 text-[12px] font-medium transition-colors ${
            mode === "whole" ? "bg-white text-ink shadow-sm" : "text-ink-subtle"
          }`}
        >
          Whole logo
        </button>
      </div>

      {mode === "whole" ? (
        <>
          <div className="flex h-72 w-full items-center justify-center rounded-[6px] bg-[#F4F7F8]">
            <img
              src={imageSrc}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <p className="mt-4 text-[11px] text-ink-subtle">
            The picture is saved exactly as uploaded — nothing cropped or
            resized.
          </p>
        </>
      ) : (
        <>
          <div className="flex h-72 w-full items-center justify-center overflow-auto rounded-[6px] bg-[#F4F7F8]">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
              keepSelection
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt=""
                onLoad={onImageLoad}
                style={{ maxHeight: 288 }}
              />
            </ReactCrop>
          </div>
          <p className="mt-2 text-[11px] text-ink-subtle">
            Drag to move the selection, or drag a handle to resize it. Saved
            at its own resolution — any shape, any size.
          </p>
        </>
      )}
    </Modal>
  );
};

export default LogoCropModal;
